import { Injectable, signal } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ApiClient, GetOrderJWTByAccessIdCommand } from 'frontend.client.api';
import { lastValueFrom } from 'rxjs';
import { BirthdayDialogComponent } from '../components/shared/birthday-dialog/birthday-dialog.component';
import { AccessHashService } from '../services/access-hash.service';
import { AccessIdService } from '../services/access-id.service';
import { RouterService } from './router.service';

@Injectable({
  providedIn: 'root',
})
export class AuthHelperService {
  private apiClient: ApiClient | undefined;
  private dialogRef: MatDialogRef<any> | undefined;
  private accessTokenStorageKey = 'accessToken';

  private title = signal('Geburtstagseingabe');
  private message = signal(
    'Ihre Daten sind geschützt. Damit Sie Ihre Daten einsehen können, benötigen wir Ihr Geburtsdatum.',
  );
  private error = signal(false);
  private loading = signal(false);
  private onSubmitHandler = signal<(value: string | undefined) => void>(
    () => { },
  );

  constructor(
    private dialog: MatDialog,
    private routerService: RouterService,
    private hashService: AccessHashService,
    private accessIdService: AccessIdService
  ) { }

  async startRefresh(): Promise<string | undefined> {
    this.hashService.removeHash();
    this.onSubmitHandler.set(
      this.error() ? this.backToStart : this.refreshToken,
    );
    this.dialogRef = this.dialog.open(BirthdayDialogComponent, {
      disableClose: true,
      width: '700px',
      data: {
        onSubmit: this.onSubmitHandler,
        title: this.title,
        message: this.message,
        isError: this.error,
        isLoading: this.loading,
      },
    });
    return await lastValueFrom(this.dialogRef.afterClosed());
  }

  backToStart = () => {
    this.dialogRef?.close();
    this.accessIdService.clearAccessId()
    this.routerService.routeToInCurrentSubfolder('unterlagenauswahl');
  };

  refreshToken = async (
    birthday: string | undefined,
  ): Promise<string | undefined> => {
    this.loading.set(true);
    this.hashService.setBirthday(birthday);
    const accessId = this.routerService.getAccessId();
    const accessHash = this.hashService.hashState$.getValue();
    if (!accessId) {
      this.backToStart();
      return;
    }
    if (!accessHash) return;

    const command = new GetOrderJWTByAccessIdCommand({
      accessId: accessId,
      access: accessHash,
    });
    try {
      const response = await this.apiClient?.send(command);
      localStorage.setItem(
        this.accessTokenStorageKey,
        response?.accessToken ?? '',
      );
      this.loading.set(false);
      this.dialogRef?.close();
      window.location.reload();
      return response?.accessToken;
    } catch (e) {
      if (e instanceof Error) {
        if (e.name === '401') {
          this.loading.set(false);
          this.title.set('Geburtstagseingabe');
          this.message.set(
            'Das eingegebene Geburtsdatum ist nicht korrekt. Bitte versuchen Sie es erneut.',
          );
          this.error.set(false);
          return;
        }
        if (e.name === '423') {
          this.error.set(true);
          this.loading.set(false);
          this.title.set('Es ist ein Fehler aufgetreten.');
          this.message.set(
            'Für die aktuelle Bestellung konnte kein hinterlegtes Geburtsdatum gefunden werden. Bitte versuchen Sie den Zugriff erneut von dem Gerät aus, auf dem Sie die Bestellung erstmalig getätigt haben.',
          );
          this.onSubmitHandler.set(this.backToStart);
          return;
        }
        return;
      }
      return;
    }
  };
  public setApiClient(apiClient: ApiClient) {
    this.apiClient = apiClient;
  }
}
